'use client';
import React, { useEffect } from 'react';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Fieldset,
  Stack,
} from '@chakra-ui/react';
import { useForm, Control, UseFormReturn, FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

export type FormControllerChildrenProps<T extends z.ZodType<any, any, any>> = {
  register: UseFormReturn<z.infer<T>>['register'];
  control: UseFormReturn<z.infer<T>>['control'];
  errors: UseFormReturn<z.infer<T>>['formState']['errors'];
  disabled?: boolean;
  disableLabel?: boolean;
  setValue?: UseFormReturn<z.infer<T>>['setValue'];
  trigger?: UseFormReturn<z.infer<T>>['trigger'];
  reset?: UseFormReturn<z.infer<T>>['reset'];
};

export interface FormControllerProps<T extends z.ZodType<any, any, any>> {
  label?: string | React.ReactNode;
  labelProps?: BoxProps;
  formSchema: T;
  helperText?: string | React.ReactNode;
  helperTextProps?: BoxProps;
  disabled?: boolean;
  disableLabels?: boolean;
  disableInputs?: boolean;
  ButtonLabel?: React.ReactNode;

  buttonProps?: ButtonProps;

  disableButton?: boolean;

  ButtonComponent?: React.ComponentType<any>;

  containerProps?: BoxProps;
  onFormSubmit: (data: z.infer<T>) => void;
  defaultValues?: Partial<z.infer<T>>;
  children: (props: FormControllerChildrenProps<T>) => React.ReactNode;

  [rest: string]: any;
}

const FormController = <T extends z.ZodType<any, any, any>>({
  label,
  formSchema,
  helperText,
  helperTextProps,
  disabled,
  disableLabels,
  disableInputs,
  onFormSubmit,
  defaultValues,
  buttonProps,
  ButtonLabel,
  disableButton,
  labelProps,
  containerProps,
  ButtonComponent,
  children,
  ...rest
}: FormControllerProps<T>) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    trigger,
  } = useForm<z.infer<T>>({
    resolver: zodResolver(formSchema as any),
    defaultValues: defaultValues as z.infer<T>,
  });

  // Reset form when defaultValues change
  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues as z.infer<T>);
    }
  }, [defaultValues, reset]);

  const onSubmit = handleSubmit(
    (data, event) => {
      event?.preventDefault();
      onFormSubmit(data);
    },
    (errors) => {
      const safeErrors = Object.fromEntries(
        Object.entries(errors).map(([key, err]) => [
          key,
          err && typeof err === 'object' && 'message' in err
            ? { message: (err as { message?: string }).message }
            : err,
        ]),
      );
      console.log(
        'Form validation failed:',
        
      );
    },
  );

  return (
    <form onSubmit={onSubmit}>
      <Fieldset.Root
        display="flex"
        flex={1}
        flexDirection="column"
        width="100%"
        minWidth={0}
        whiteSpace="normal"
        {...rest}
        disabled={disabled}
      >
        {label && (
          <Fieldset.Legend
            textStyle="heading.form"
            color="secondary.fg"
            {...labelProps}
          >
            {label}
          </Fieldset.Legend>
        )}
        {helperText && (
          <Fieldset.HelperText
            color={disableInputs || disabled ? 'fg.warning' : 'fg.subtle'}
            alignSelf="flex-start"
            textAlign="center"
            {...helperTextProps}
          >
            {`${disableInputs || disabled ? '⚠︎' : ''} ${helperText}`}
          </Fieldset.HelperText>
        )}
        <Fieldset.Content
          flex={1}
          height="100%"
          width="100%"
          minWidth={0}
          overflow="auto"
          {...containerProps}
        >
          {children({
            register,
            control,
            errors,
            disabled: disableInputs || disabled,
            disableLabel: disableLabels,
            setValue,
            trigger,
            reset,
          })}
        </Fieldset.Content>
        <Fieldset.ErrorText>
          {Object.keys(errors).length > 0 && (
            <span>Please fix the errors.</span>
          )}
        </Fieldset.ErrorText>
        {ButtonComponent ? (
          React.createElement(ButtonComponent, {
            variant: 'outline',
            type: 'submit',
            textStyle: 'button',
            mt: '4',
            color: 'secondary.solid',
            borderColor: 'secondary.emphasized',
            alignSelf: 'flex-start',
            whiteSpace: 'nowrap',
            ...buttonProps,
            disabled: disableButton || disabled,
            onClick: onSubmit,
          })
        ) : (
          <Button
            variant="outline"
            type="submit"
            textStyle="button"
            mt="4"
            disabled={disableButton || disabled}
            color="secondary.solid"
            borderColor="secondary.emphasized"
            alignSelf="center"
            onClick={onSubmit}
            whiteSpace="nowrap"
            {...buttonProps}
          >
            {ButtonLabel || 'Submit'}
          </Button>
        )}
      </Fieldset.Root>
    </form>
  );
};

export default FormController;
